import { colors, r8, s8 } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { Header4 } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { DatasourceTable, newTableTheme } from '@ardoq/table-legacy';
import {
  ITPediaSchedule,
  IntegrationWorkspace,
} from '@ardoq/api-types/integrations';
import { ArdoqId } from '@ardoq/api-types';
import { Icon, IconName } from '@ardoq/icons';
import { WithPopover } from '@ardoq/popovers';
import { FlexBox, Stack } from '@ardoq/layout';
import { useMemo } from 'react';
import { type I18nLocale, t } from '@ardoq/i18n';
import { connect } from '@ardoq/rxbeach';
import { distinctUntilChanged, map } from 'rxjs';
import { isEqual } from 'lodash';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';

type ExpandedRowParams = {
  schedule: ITPediaSchedule;
  onWorkspaceClick: ((workspace: IntegrationWorkspace) => void) | null;
  i18nLocale: I18nLocale;
};

type ExpandedRowViewParams = ExpandedRowParams & {
  loadedWorkspaceIds: ReadonlySet<ArdoqId>;
};

const toLoadedWorkspaceIds = (
  sortIds: readonly ArdoqId[]
): { loadedWorkspaceIds: ReadonlySet<ArdoqId> } => ({
  loadedWorkspaceIds: new Set(sortIds),
});

function ITPediaDetailsBase({
  schedule,
  onWorkspaceClick,
  i18nLocale,
  loadedWorkspaceIds,
}: ExpandedRowViewParams) {
  const { completed, enrolled, progress } = useMemo(() => {
    if (!schedule.meta.lastResult?.summary) {
      return { completed: 0, enrolled: 0, progress: 0 };
    }

    const completed = schedule.meta.lastResult.summary.completed.products;
    const enrolled = schedule.meta.lastResult.summary.enrolled.products;
    const progress = (completed / (enrolled > 0 ? enrolled : 1)) * 100;

    return { completed, enrolled, progress };
  }, [schedule]);

  const tableDataSource = useMemo(() => {
    return [
      {
        componentTypes: Object.keys(schedule.jobOptions.componentTypes).join(
          ','
        ),
        workspace: schedule.jobOptions.workspaces.products,
      },
      {
        componentTypes: 'Vulnerability',
        workspace: schedule.jobOptions.workspaces.vulnerabilities,
      },
    ];
  }, [schedule]);

  return (
    <Stack gap="small">
      <FlexBox align="center" gap="small">
        <Header4>
          {t(
            i18nLocale,
            'integrations:itpediaDetails.enrichingProgress',
            'Enriching progress'
          )}
        </Header4>
        <ProgressNumbers>
          {t(
            i18nLocale,
            'integrations:itpediaDetails.completedOfEnrolled',
            { completed, enrolled },
            '{{completed}} of {{enrolled}}'
          )}
        </ProgressNumbers>
        <Progress percentage={progress} />
      </FlexBox>
      <DatasourceTable
        dataSource={tableDataSource}
        components={newTableTheme}
        columns={[
          {
            title: t(
              i18nLocale,
              'integrations:itpediaDetails.componentTypes',
              'Component types'
            ),
            dataIndex: 'componentTypes',
          },
          {
            title: t(
              i18nLocale,
              'integrations:itpediaDetails.workspace',
              'Workspace'
            ),
            dataIndex: 'workspace',
            valueRender: workspace => {
              // Vulnerabilities can have undefined workspace
              if (!workspace) return <></>;
              if (!workspace.id || !loadedWorkspaceIds.has(workspace.id)) {
                return (
                  <WithPopover
                    content={t(
                      i18nLocale,
                      'integrations:itpediaDetails.workspaceNotAvailable',
                      'Workspace is not available'
                    )}
                  >
                    <FlexBox align="center" gap="xxsmall">
                      {workspace.name}
                      <Icon iconName={IconName.INFO} color={colors.grey500} />
                    </FlexBox>
                  </WithPopover>
                );
              }
              return onWorkspaceClick ? (
                <Link
                  iconName={IconName.WORKSPACE}
                  type="primary"
                  typography="text1"
                  onClick={() => onWorkspaceClick(workspace)}
                >
                  {workspace.name}
                </Link>
              ) : (
                workspace.name
              );
            },
          },
        ]}
      />
    </Stack>
  );
}

export const ITPediaDetails = connect(
  ITPediaDetailsBase,
  workspaces$.pipe(
    map(workspaces => workspaces.sortIds),
    distinctUntilChanged(isEqual),
    map(toLoadedWorkspaceIds)
  )
);

const ProgressNumbers = styled.div`
  text-wrap: nowrap;
`;

// We can't use the progress component from design systems
// this is because that will always put a percentage instead
// actual numbers
const Progress = styled.div<{ percentage: number }>`
  width: 128px;
  background-color: ${colors.surfaceDefault};
  height: ${s8};
  border-radius: ${r8};
  position: relative;
  &::before {
    position: absolute;
    width: ${({ percentage }) => percentage}%;
    border-radius: ${r8};
    background-color: ${colors.blue700};
    content: ' ';
    height: 100%;
  }
`;
